import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, Loader2, X, ChevronDown, ChevronUp, ExternalLink, Pencil, Trash2, Plus, Search, FileText, Copy } from "lucide-react";
import { API, authHeaders, toastErr } from "@/components/admin/AdminPage";

const STATUSES = ["Order Confirmed", "Generating", "Delivered"];
const statusCls = {
  "Order Confirmed": "bg-sunny/40",
  "Generating": "bg-cobalt/15",
  "Delivered": "bg-mint/30",
};

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmt = (ts) => (ts ? new Date(ts).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

const NEW_ORDER_KEYS = ["buyer_name", "buyer_email", "buyer_phone", "recipient_name", "relationship", "relationship_other", "occasion", "key_memories", "emotional_tone", "genre", "vocal_style", "language", "delivery_date"];
const TEXTAREA_KEYS = new Set(["key_memories"]);

export default function AdminOrders({ token }) {
  const [orders, setOrders] = useState([]);
  const [delivering, setDelivering] = useState(null);
  const [editing, setEditing] = useState(null);
  const [promptFor, setPromptFor] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [query, setQuery] = useState("");

  const load = () =>
    axios.get(`${API}/admin/orders`, authHeaders(token))
      .then((r) => setOrders(r.data))
      .catch((e) => toastErr(e, "Could not load orders"));

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) => {
      const d = o.details || {};
      return [o.order_ref, o.order_id, d.buyer_name, d.buyer_email, d.buyer_phone, d.recipient_name, d.occasion, d.genre, o.status, o.order_status]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [orders, query]);

  const setStatus = async (orderId, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}`, { status }, authHeaders(token));
      toast.success(`Status → ${status}`);
      load();
    } catch (e) { toastErr(e); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete order ${o.order_ref || ""} for ${o.details?.recipient_name || "this customer"}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/orders/${o.order_id}`, authHeaders(token));
      toast.success("Order deleted");
      load();
    } catch (e) { toastErr(e, "Delete failed"); }
  };

  const toggle = (id) => setExpanded((x) => ({ ...x, [id]: !x[id] }));

  return (
    <div data-testid="admin-orders-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm font-medium text-plum">{filtered.length} of {orders.length} order{orders.length === 1 ? "" : "s"}</p>
        <div className="flex items-center gap-3 flex-1 sm:flex-none justify-end">
          <div className="relative flex-1 sm:flex-none sm:w-72">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-lilac" />
            <input
              data-testid="orders-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search order ID, name, email, occasion…"
              className="w-full rounded-full bg-white border-2 border-ink pl-9 pr-4 py-2 text-sm font-medium focus:outline-none focus:shadow-[3px_3px_0_#FF3B7E] transition-shadow"
            />
          </div>
          <button data-testid="add-order-btn" onClick={() => setEditing("new")}
            className="btn-pop inline-flex items-center gap-1.5 rounded-full bg-sunny border-2 border-ink px-5 py-2 text-sm font-head font-bold shadow-[3px_3px_0_#2D1A32]">
            <Plus size={15} /> Add Order
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-plum font-medium text-sm">{orders.length === 0 ? "No orders yet — they'll appear here the moment someone submits the form." : "No orders match your search."}</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl bg-white border-[3px] border-ink shadow-[6px_6px_0_#2D1A32]">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b-[3px] border-ink bg-cream font-head text-xs uppercase tracking-wider text-plum">
                <th className="p-4 w-8" />
                <th className="p-4">Order</th>
                <th className="p-4">Customer</th>
                <th className="p-4">Song For</th>
                <th className="p-4">Occasion / Genre</th>
                <th className="p-4">Amount</th>
                <th className="p-4">Payment</th>
                <th className="p-4">Status</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const d = o.details || {};
                const isOpen = !!expanded[o.order_id];
                const detailFields = Object.entries(d).filter(([, v]) => v !== "" && v != null);
                const rowId = (o.order_ref || o.order_id).slice(-8);
                return [
                  <tr key={o.order_id} className="border-b border-ink/10" data-testid={`admin-order-row-${rowId}`}>
                    <td className="p-4">
                      <button data-testid={`order-expand-btn-${rowId}`} onClick={() => toggle(o.order_id)} aria-label="Expand order"
                        className="w-7 h-7 rounded-full border-2 border-ink bg-cream flex items-center justify-center">
                        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    </td>
                    <td className="p-4 whitespace-nowrap">
                      <p className="font-head font-bold text-pink" data-testid={`order-ref-${rowId}`}>{o.order_ref || "—"}</p>
                      <p className="font-mono text-[10px] text-lilac mt-0.5">{fmt(o.created_at)}</p>
                    </td>
                    <td className="p-4">
                      <p className="font-head font-bold">{d.buyer_name || "—"}</p>
                      <p className="text-xs text-lilac">{d.buyer_email}</p>
                      <p className="text-xs text-lilac">{d.buyer_phone}</p>
                    </td>
                    <td className="p-4 font-medium">{d.recipient_name || "—"}</td>
                    <td className="p-4 text-xs font-medium text-plum">{d.occasion || "—"}<br />{d.genre || ""}</td>
                    <td className="p-4 font-head font-bold">{inr(o.amount)}</td>
                    <td className="p-4">
                      <span className={`inline-block rounded-full border border-ink px-2.5 py-1 text-[10px] font-mono uppercase ${o.status === "paid" ? "bg-mint/30" : "bg-cream"}`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="p-4">
                      <select
                        data-testid={`admin-status-select-${rowId}`}
                        value={o.order_status || "Order Confirmed"}
                        onChange={(e) => setStatus(o.order_id, e.target.value)}
                        className={`rounded-full border-2 border-ink px-3 py-1.5 text-xs font-head font-bold ${statusCls[o.order_status] || "bg-sunny/40"}`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <button data-testid={`admin-prompt-btn-${rowId}`} onClick={() => setPromptFor(o)} aria-label="Song generation prompt"
                          className="btn-pop w-8 h-8 rounded-full bg-cobalt/15 border-2 border-ink flex items-center justify-center shadow-[2px_2px_0_#2D1A32]">
                          <FileText size={13} />
                        </button>
                        <button data-testid={`admin-edit-btn-${rowId}`} onClick={() => setEditing(o)} aria-label="Edit order"
                          className="btn-pop w-8 h-8 rounded-full bg-white border-2 border-ink flex items-center justify-center shadow-[2px_2px_0_#2D1A32]">
                          <Pencil size={13} />
                        </button>
                        {o.song_path ? (
                          <a href={`${API}/files/${o.song_path}`} data-testid={`admin-song-link-${rowId}`} aria-label="Song sent"
                            className="w-8 h-8 rounded-full bg-mint/40 border-2 border-ink flex items-center justify-center text-[10px] font-bold">✓</a>
                        ) : (
                          <button data-testid={`admin-deliver-btn-${rowId}`} onClick={() => setDelivering(o)}
                            className="btn-pop rounded-full bg-mint border-2 border-ink px-3.5 py-1.5 text-xs font-head font-bold shadow-[2px_2px_0_#2D1A32]">
                            Deliver
                          </button>
                        )}
                        <button data-testid={`admin-delete-btn-${rowId}`} onClick={() => remove(o)} aria-label="Delete order"
                          className="btn-pop w-8 h-8 rounded-full bg-pink text-white border-2 border-ink flex items-center justify-center shadow-[2px_2px_0_#2D1A32]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>,
                  isOpen && (
                    <tr key={o.order_id + "-detail"} className="border-b border-ink/10 bg-cardalt/60" data-testid={`order-detail-${rowId}`}>
                      <td colSpan={9} className="p-5">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {detailFields.map(([k, v]) => (
                            <div key={k} className="rounded-xl bg-white border-2 border-ink/15 p-3">
                              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-lilac">{k.replace(/_/g, " ")}</p>
                              <p className="text-xs font-medium text-ink mt-1 whitespace-pre-wrap break-words">{String(v)}</p>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 font-mono text-[10px] text-lilac">
                          <span>Order: {o.order_ref || o.order_id}</span>
                          <span>Created: {fmt(o.created_at)}</span>
                          {o.paid_at && <span>Paid: {fmt(o.paid_at)}</span>}
                          {o.delivered_at && <span>Delivered: {fmt(o.delivered_at)}</span>}
                          {(o.phonepe_transaction_id || o.razorpay_payment_id) && <span>Payment: {o.phonepe_transaction_id || o.razorpay_payment_id}</span>}
                          <a href={`/track/${o.order_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cobalt underline underline-offset-2">
                            Tracking page <ExternalLink size={10} />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
      {delivering && <DeliverModal order={delivering} token={token} onClose={() => setDelivering(null)} onDone={() => { setDelivering(null); load(); }} />}
      {editing && <OrderModal order={editing === "new" ? null : editing} token={token} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
      {promptFor && <PromptModal order={promptFor} token={token} onClose={() => setPromptFor(null)} />}
    </div>
  );
}

function PromptModal({ order, token, onClose }) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/orders/${order.order_id}/prompt`, authHeaders(token))
      .then((r) => setPrompt(r.data.prompt))
      .catch((e) => toastErr(e, "Could not build prompt"))
      .finally(() => setLoading(false));
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      toast.success("Prompt copied — paste it into your music AI");
    } catch {
      toast.error("Copy failed — select the text and copy manually");
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/50 backdrop-blur-sm p-4" onClick={onClose} data-testid="prompt-modal">
      <div className="w-full max-w-2xl rounded-3xl bg-white border-[3px] border-ink shadow-[8px_8px_0_#2D1A32] p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display font-bold text-lg">Song Generation Prompt</h3>
            <p className="text-xs text-plum font-medium mt-1">Order {order.order_ref || order.order_id} — paste this into Suno or any music AI</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="btn-pop w-9 h-9 rounded-full border-2 border-ink bg-cream flex items-center justify-center shrink-0">
            <X size={16} />
          </button>
        </div>
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-plum font-medium py-10 justify-center"><Loader2 size={16} className="animate-spin" /> Building prompt…</p>
        ) : (
          <>
            <textarea readOnly value={prompt} rows={14} data-testid="prompt-textarea"
              className="w-full rounded-xl bg-cream border-2 border-ink px-4 py-3 text-xs font-mono leading-relaxed resize-none focus:outline-none" />
            <button data-testid="prompt-copy-btn" onClick={copy}
              className="btn-pop mt-4 inline-flex items-center gap-2 rounded-full bg-pink text-white font-head font-bold px-6 py-3 text-sm border-2 border-ink shadow-[4px_4px_0_#2D1A32]">
              <Copy size={15} /> Copy prompt
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function OrderModal({ order, token, onClose, onDone }) {
  const isNew = !order;
  const [fields, setFields] = useState(() => {
    if (isNew) return Object.fromEntries(NEW_ORDER_KEYS.map((k) => [k, ""]));
    return Object.fromEntries(Object.entries(order.details || {}).map(([k, v]) => [k, String(v ?? "")]));
  });
  const [amount, setAmount] = useState(order?.amount ?? "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const details = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== ""));
      const body = { details, amount: amount === "" ? null : Number(amount) };
      if (isNew) {
        const { data } = await axios.post(`${API}/admin/orders`, body, authHeaders(token));
        toast.success(`Order ${data.order_ref || ""} added`);
      } else {
        await axios.put(`${API}/admin/orders/${order.order_id}`, body, authHeaders(token));
        toast.success("Order updated");
      }
      onDone();
    } catch (e) {
      toastErr(e, "Save failed");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/50 backdrop-blur-sm p-4" data-testid="order-edit-modal" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white border-[3px] border-ink shadow-[8px_8px_0_#2D1A32] p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-xl">{isNew ? "Add Order" : `Edit ${order.order_ref || "order"} — ${order.details?.recipient_name || ""}`}</h3>
          <button data-testid="order-edit-close" onClick={onClose} className="w-8 h-8 rounded-full border-2 border-ink bg-cream flex items-center justify-center"><X size={14} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {Object.keys(fields).map((k) => (
            <div key={k} className={TEXTAREA_KEYS.has(k) ? "sm:col-span-2" : ""}>
              <label className="block font-head font-bold text-[11px] text-plum mb-1 capitalize">{k.replace(/_/g, " ")}</label>
              {TEXTAREA_KEYS.has(k) ? (
                <textarea data-testid={`order-edit-field-${k}`} value={fields[k]} onChange={(e) => setFields((f) => ({ ...f, [k]: e.target.value }))} rows={4}
                  className="w-full rounded-xl bg-cream border-2 border-ink px-3 py-2 text-sm font-medium resize-none focus:outline-none focus:shadow-[3px_3px_0_#FF3B7E] transition-shadow" />
              ) : (
                <input data-testid={`order-edit-field-${k}`} value={fields[k]} onChange={(e) => setFields((f) => ({ ...f, [k]: e.target.value }))}
                  className="w-full rounded-xl bg-cream border-2 border-ink px-3 py-2 text-sm font-medium focus:outline-none focus:shadow-[3px_3px_0_#FF3B7E] transition-shadow" />
              )}
            </div>
          ))}
          <div>
            <label className="block font-head font-bold text-[11px] text-plum mb-1">Amount (₹)</label>
            <input data-testid="order-edit-field-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-xl bg-cream border-2 border-ink px-3 py-2 text-sm font-medium focus:outline-none focus:shadow-[3px_3px_0_#FF3B7E] transition-shadow" />
          </div>
        </div>
        <button data-testid="order-edit-save-btn" onClick={save} disabled={busy}
          className="btn-pop mt-6 w-full rounded-full bg-pink text-white font-head font-bold py-3 border-2 border-ink shadow-[4px_4px_0_#2D1A32] disabled:opacity-50 flex items-center justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />}
          {isNew ? "Add Order" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function DeliverModal({ order, token, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [lyrics, setLyrics] = useState("");
  const [busy, setBusy] = useState(false);
  const d = order.details || {};

  const submit = async () => {
    if (!file) return toast.error("Please attach the MP3 file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("lyrics", lyrics);
      await axios.post(`${API}/admin/orders/${order.order_id}/deliver`, fd, authHeaders(token));
      toast.success(`Delivered & emailed to ${d.buyer_email}`);
      onDone();
    } catch (e) {
      toastErr(e, "Delivery failed");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/50 backdrop-blur-sm p-4" data-testid="deliver-modal" onClick={onClose}>
      <div className="w-full max-w-lg rounded-3xl bg-white border-[3px] border-ink shadow-[8px_8px_0_#2D1A32] p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-xl">Deliver song for {d.recipient_name}</h3>
          <button data-testid="deliver-modal-close" onClick={onClose} className="w-8 h-8 rounded-full border-2 border-ink bg-cream flex items-center justify-center"><X size={14} /></button>
        </div>
        <p className="text-xs text-lilac font-medium mb-4">Goes to <span className="font-bold text-ink">{d.buyer_email}</span> with the MP3, a printable lyric-card PDF and the lyrics.</p>
        <label className="block font-head font-bold text-xs text-plum mb-1.5">MP3 file *</label>
        <label className="flex items-center gap-3 rounded-xl border-2 border-dashed border-ink bg-cream px-4 py-4 cursor-pointer hover:bg-cardalt transition-colors">
          <UploadCloud size={20} className="text-pink" />
          <span className="text-sm font-medium text-plum">{file ? file.name : "Choose the finished song…"}</span>
          <input data-testid="deliver-file-input" type="file" accept="audio/mpeg,audio/mp3,audio/*" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
        </label>
        <label className="block font-head font-bold text-xs text-plum mb-1.5 mt-4">Song lyrics (goes in the email + lyric-card PDF)</label>
        <textarea data-testid="deliver-lyrics-input" value={lyrics} onChange={(e) => setLyrics(e.target.value)} rows={6}
          placeholder="Paste the full lyrics here…"
          className="w-full rounded-xl bg-cream border-2 border-ink px-4 py-3 text-sm font-medium focus:outline-none focus:shadow-[3px_3px_0_#FF3B7E] transition-shadow resize-none" />
        <button data-testid="admin-delivery-submit-btn" onClick={submit} disabled={busy}
          className="btn-pop mt-5 w-full rounded-full bg-pink text-white font-head font-bold py-3 border-2 border-ink shadow-[4px_4px_0_#2D1A32] disabled:opacity-50 flex items-center justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />}
          {busy ? "Uploading & emailing…" : "Deliver & Email Customer"}
        </button>
      </div>
    </div>
  );
}
